import { useState, useCallback } from 'react';
import { getErrorMessage, logError } from '../../lib/errors';

interface UseErrorHandlerOptions {
  defaultMessage?: string;
  logContext?: Record<string, any>;
  onError?: (error: unknown) => void;
}

export function useErrorHandler(options: UseErrorHandlerOptions = {}) {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleError = useCallback((error: unknown) => {
    const message = getErrorMessage(error);
    setError(options.defaultMessage || message);
    
    logError(error, options.logContext);
    
    if (options.onError) {
      options.onError(error);
    }
  }, [options]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const execute = useCallback(async <T,>(
    fn: () => Promise<T>,
    options?: {
      onSuccess?: (result: T) => void;
      onError?: (error: unknown) => void;
    }
  ): Promise<T | undefined> => {
    setError(null);
    setIsLoading(true);

    try {
      const result = await fn();
      if (options?.onSuccess) {
        options.onSuccess(result);
      }
      return result;
    } catch (error) {
      handleError(error);
      if (options?.onError) {
        options.onError(error);
      }
      return undefined;
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  return {
    error,
    isLoading,
    handleError,
    clearError,
    execute,
  };
}

/**
 * Hook for handling form errors with field-level validation
 */
export function useFormErrorHandler<T extends Record<string, any>>() {
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);

  const setFieldError = useCallback((field: keyof T, message: string) => {
    setErrors(prev => ({ ...prev, [field]: message }));
  }, []);

  const clearFieldError = useCallback((field: keyof T) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors({});
    setGeneralError(null);
  }, []);

  const handleValidationError = useCallback((error: unknown) => {
    if (error && typeof error === 'object' && 'details' in error) {
      const details = (error as any).details;
      if (details && typeof details === 'object') {
        // Set field-level errors
        Object.entries(details).forEach(([field, message]) => {
          if (typeof message === 'string') {
            setFieldError(field as keyof T, message);
          }
        });
        return;
      }
    }
    
    // Set general error if not field-specific
    setGeneralError(getErrorMessage(error));
  }, [setFieldError]);

  return {
    errors,
    generalError,
    setFieldError,
    clearFieldError,
    clearAllErrors,
    handleValidationError,
    hasErrors: Object.keys(errors).length > 0 || !!generalError,
  };
}